import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Alert, Badge, Button, ButtonGroup, Card, CardBody, Col, Form, FormGroup,
  Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Nav, NavItem,
  NavLink, Row, TabContent, TabPane,
} from 'reactstrap';
import { PageHeader } from '../../components/PageHeader';
import { DataTable } from '../../components/DataTable';
import { RelativeTime } from '../../components/RelativeTime';
import { HasPermission } from '../../rbac/HasPermission';
import { useHasPermission } from '../../rbac/useHasPermission';
import { isMtffProxyEnabled } from '../../mtff/mtffClient';
import {
  useAddAllowlistEntry,
  useDeleteAllowlistEntry,
  useMtffInstance,
  useMtffSecurity,
  useMtffSecurityEvents,
  useSaveMtffSecurity,
} from './useMtff';

/**
 * MTFF Security pro Instance — Spec README §10.6.
 *
 * Vier Tabs:
 *   1. Overview      — Modus-Switch + Rate-Limits
 *   2. Allowlist     — IP/CIDR-Liste mit Reason + TTL
 *   3. AppSec-Rules  — Per-Rule-ID Override (allow/block/detect)
 *   4. Event Explorer— Read-only Stream der Security-Events (30s-Poll)
 */

const MODES = [
  { value: 'off',    label: 'off    — keine AppSec-Aktion' },
  { value: 'detect', label: 'detect — loggen, nicht blocken (Default)' },
  { value: 'block',  label: 'block  — blocken' },
  { value: 'strict', label: 'strict — block + Pulse-Alert' },
];

export function MtffSecurityPage() {
  const { instanceId } = useParams();
  const enabled = isMtffProxyEnabled();
  const canWrite = useHasPermission({ perms: ['integrations.write'] });
  const instance = useMtffInstance(instanceId);
  const [tab, setTab] = useState('overview');

  if (!enabled) {
    return (
      <div>
        <PageHeader title="MTFF · Security" />
        <Alert color="warning" className="small">MTFF-Admin-Proxy nicht konfiguriert.</Alert>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title={`MTFF · Security · ${instance.data?.tenantId ?? '…'}/${instance.data?.instanceId ?? '…'}`}
        subtitle="Modus + Allowlist + AppSec-Rules + Event-Explorer"
        actions={
          <Button color="secondary" outline size="sm" tag={Link} to="/admin/settings/mtff/instances">
            ← Instances
          </Button>
        }
      />

      <Nav tabs className="mb-3">
        {[
          ['overview', 'Overview'],
          ['allowlist', 'Allowlist'],
          ['rules', 'AppSec-Rules'],
          ['events', 'Event-Explorer'],
        ].map(([id, label]) => (
          <NavItem key={id}>
            <NavLink active={tab === id} onClick={() => setTab(id)} role="button">
              {label}
            </NavLink>
          </NavItem>
        ))}
      </Nav>

      <TabContent activeTab={tab}>
        <TabPane tabId="overview">
          {tab === 'overview' && <OverviewTab instanceId={instanceId} canWrite={canWrite} />}
        </TabPane>
        <TabPane tabId="allowlist">
          {tab === 'allowlist' && <AllowlistTab instanceId={instanceId} canWrite={canWrite} />}
        </TabPane>
        <TabPane tabId="rules">
          {tab === 'rules' && <RulesTab instanceId={instanceId} canWrite={canWrite} />}
        </TabPane>
        <TabPane tabId="events">
          {tab === 'events' && <EventsTab instanceId={instanceId} />}
        </TabPane>
      </TabContent>
    </div>
  );
}

/* ── Overview ──────────────────────────────────────────────────────── */

function OverviewTab({ instanceId, canWrite }) {
  const query = useMtffSecurity(instanceId);
  if (!query.data) return <p className="small text-muted">Lade…</p>;
  return (
    <OverviewForm
      key={`${instanceId}-overview`}
      instanceId={instanceId}
      initial={query.data}
      canWrite={canWrite}
    />
  );
}

function OverviewForm({ instanceId, initial, canWrite }) {
  const save = useSaveMtffSecurity();
  const [form, setForm] = useState(() => ({
    appsecMode: initial.appsecMode ?? 'detect',
    perMinute: initial.rateLimits?.perMinute ?? 600,
    perHour: initial.rateLimits?.perHour ?? 18000,
  }));

  const onSubmit = async (e) => {
    e.preventDefault();
    await save.mutateAsync({
      instanceId,
      appsecMode: form.appsecMode,
      rateLimits: {
        perMinute: Number(form.perMinute) || 0,
        perHour: Number(form.perHour) || 0,
      },
    });
  };

  return (
    <Row className="g-3">
      <Col lg="6">
        <Card><CardBody>
          <h6 className="text-uppercase small text-muted mb-2">Mode + Rate-Limits</h6>
          <Form onSubmit={onSubmit}>
            <FormGroup>
              <Label className="small">AppSec-Mode</Label>
              <Input
                type="select" bsSize="sm" value={form.appsecMode}
                onChange={(e) => setForm((f) => ({ ...f, appsecMode: e.target.value }))}
                disabled={!canWrite}
              >
                {MODES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
              </Input>
            </FormGroup>
            <Row className="g-2">
              <Col>
                <FormGroup>
                  <Label className="small">Requests/Minute</Label>
                  <Input
                    type="number" bsSize="sm" value={form.perMinute}
                    onChange={(e) => setForm((f) => ({ ...f, perMinute: e.target.value }))}
                    disabled={!canWrite}
                  />
                </FormGroup>
              </Col>
              <Col>
                <FormGroup>
                  <Label className="small">Requests/Stunde</Label>
                  <Input
                    type="number" bsSize="sm" value={form.perHour}
                    onChange={(e) => setForm((f) => ({ ...f, perHour: e.target.value }))}
                    disabled={!canWrite}
                  />
                </FormGroup>
              </Col>
            </Row>
            <HasPermission perms={['integrations.write']}>
              <Button color="primary" type="submit" size="sm" disabled={!canWrite || save.isPending}>
                {save.isPending ? 'Speichere…' : '💾 Speichern'}
              </Button>
            </HasPermission>
          </Form>
        </CardBody></Card>
      </Col>
      <Col lg="6">
        <Card><CardBody>
          <h6 className="text-uppercase small text-muted mb-2">Modus-Erklärung</h6>
          <dl className="small mb-0">
            <dt><code>off</code></dt>
            <dd>AppSec wird komplett übersprungen. Nicht für Prod-Brands.</dd>
            <dt><code>detect</code></dt>
            <dd>Verdächtige Requests landen im Event-Log, werden aber nicht blockiert. Default für frische Instances damit Tuning ohne Customer-Impact passieren kann.</dd>
            <dt><code>block</code></dt>
            <dd>Wie detect plus Block. Standard für Prod nach Tuning-Phase.</dd>
            <dt><code>strict</code></dt>
            <dd>Block plus Pulse-Alert. Nur für hochsensitive Instances (Checkout-only-Brands).</dd>
          </dl>
        </CardBody></Card>
      </Col>
    </Row>
  );
}

/* ── Allowlist ─────────────────────────────────────────────────────── */

function AllowlistTab({ instanceId, canWrite }) {
  const query = useMtffSecurity(instanceId);
  const del = useDeleteAllowlistEntry();
  const [addOpen, setAddOpen] = useState(false);
  const list = query.data?.allowlist ?? [];

  return (
    <Card><CardBody>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h6 className="text-uppercase small text-muted mb-0">Allowlist</h6>
        <HasPermission perms={['integrations.write']}>
          <Button size="sm" color="primary" onClick={() => setAddOpen(true)} disabled={!canWrite}>
            + Eintrag
          </Button>
        </HasPermission>
      </div>
      <DataTable
        loading={query.isLoading}
        empty={!query.isLoading && list.length === 0}
        emptyMessage="Keine Allowlist-Einträge."
        onRetry={() => query.refetch()}
      >
        <thead>
          <tr>
            <th style={{ width: 200 }}>IP / CIDR</th>
            <th>Reason</th>
            <th style={{ width: 180 }}>Läuft ab</th>
            <th style={{ width: 80 }} />
          </tr>
        </thead>
        <tbody>
          {list.map((e) => (
            <tr key={e.id}>
              <td className="small font-monospace">
                {e.ip}{e.cidr ? `/${e.cidr}` : ''}
              </td>
              <td className="small">{e.reason ?? '—'}</td>
              <td className="small">
                {e.expiresAt
                  ? new Date(e.expiresAt).toLocaleString('de-DE')
                  : <span className="text-muted">unbefristet</span>}
              </td>
              <td>
                <HasPermission perms={['integrations.write']}>
                  <Button
                    size="sm" color="danger" outline
                    onClick={() => del.mutate({ instanceId, entryId: e.id })}
                    disabled={!canWrite || del.isPending}
                  >
                    ✕
                  </Button>
                </HasPermission>
              </td>
            </tr>
          ))}
        </tbody>
      </DataTable>
      {addOpen && (
        <AddAllowlistModal instanceId={instanceId} onClose={() => setAddOpen(false)} />
      )}
    </CardBody></Card>
  );
}

function AddAllowlistModal({ instanceId, onClose }) {
  const add = useAddAllowlistEntry();
  const [form, setForm] = useState({ ip: '', cidr: '', reason: '', expiresAt: '' });

  const onSubmit = async (e) => {
    e.preventDefault();
    const body = { ip: form.ip.trim(), reason: form.reason.trim() };
    if (form.cidr) body.cidr = Number(form.cidr);
    if (form.expiresAt) body.expiresAt = new Date(form.expiresAt).toISOString();
    await add.mutateAsync({ instanceId, ...body });
    onClose();
  };

  return (
    <Modal isOpen toggle={onClose}>
      <ModalHeader toggle={onClose}>Allowlist-Eintrag</ModalHeader>
      <Form onSubmit={onSubmit}>
        <ModalBody>
          <FormGroup>
            <Label className="small">IP</Label>
            <Input
              type="text" bsSize="sm" value={form.ip}
              onChange={(e) => setForm((f) => ({ ...f, ip: e.target.value }))}
              placeholder="198.51.100.10"
              required
            />
          </FormGroup>
          <FormGroup>
            <Label className="small">CIDR (optional)</Label>
            <Input
              type="number" min="0" max="32" bsSize="sm" value={form.cidr}
              onChange={(e) => setForm((f) => ({ ...f, cidr: e.target.value }))}
              placeholder="24 für /24"
            />
          </FormGroup>
          <FormGroup>
            <Label className="small">Reason</Label>
            <Input
              type="text" bsSize="sm" value={form.reason}
              onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
              placeholder="Office NAT"
              required
            />
          </FormGroup>
          <FormGroup>
            <Label className="small">Läuft ab (optional)</Label>
            <Input
              type="datetime-local" bsSize="sm" value={form.expiresAt}
              onChange={(e) => setForm((f) => ({ ...f, expiresAt: e.target.value }))}
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={onClose}>Abbrechen</Button>
          <Button color="primary" type="submit" disabled={add.isPending}>
            {add.isPending ? 'Lege an…' : 'Anlegen'}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
}

/* ── AppSec-Rules ──────────────────────────────────────────────────── */

function RulesTab({ instanceId, canWrite }) {
  const query = useMtffSecurity(instanceId);
  if (!query.data) return <p className="small text-muted">Lade…</p>;
  return (
    <RulesForm
      key={`${instanceId}-rules`}
      instanceId={instanceId}
      initial={query.data}
      canWrite={canWrite}
    />
  );
}

function RulesForm({ instanceId, initial, canWrite }) {
  const save = useSaveMtffSecurity();
  const [text, setText] = useState(() =>
    JSON.stringify(initial.ruleOverrides ?? {}, null, 2),
  );

  const onSubmit = async (e) => {
    e.preventDefault();
    let ruleOverrides;
    try { ruleOverrides = JSON.parse(text); }
    catch (err) {
      // eslint-disable-next-line no-alert
      alert('JSON ungültig: ' + err.message);
      return;
    }
    await save.mutateAsync({
      instanceId,
      appsecMode: initial.appsecMode,
      rateLimits: initial.rateLimits,
      ruleOverrides,
    });
  };

  return (
    <Card><CardBody>
      <h6 className="text-uppercase small text-muted mb-2">Per-Rule-ID Overrides</h6>
      <p className="small text-muted">
        Map <code>{'{ "ruleId": "allow"|"block"|"detect" }'}</code>. Rule-IDs
        kommen aus dem AppSec-Backend (CRS-Style). Override gilt nur für
        diese Instance.
      </p>
      <Form onSubmit={onSubmit}>
        <Input
          type="textarea"
          rows={12}
          value={text}
          onChange={(e) => setText(e.target.value)}
          spellCheck={false}
          style={{ fontFamily: 'var(--bs-font-monospace)', fontSize: 13 }}
          disabled={!canWrite}
        />
        <HasPermission perms={['integrations.write']}>
          <Button color="primary" type="submit" size="sm" className="mt-2" disabled={!canWrite || save.isPending}>
            {save.isPending ? 'Speichere…' : '💾 Speichern'}
          </Button>
        </HasPermission>
      </Form>
    </CardBody></Card>
  );
}

/* ── Events ────────────────────────────────────────────────────────── */

function EventsTab({ instanceId }) {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const query = useMtffSecurityEvents(instanceId, { from, to });
  const list = query.data?.events ?? [];

  return (
    <Card><CardBody>
      <div className="d-flex justify-content-between align-items-end mb-2 flex-wrap gap-2">
        <h6 className="text-uppercase small text-muted mb-0">Event-Explorer</h6>
        <div className="d-flex gap-2 align-items-end">
          <FormGroup className="mb-0">
            <Label className="small">Von</Label>
            <Input type="datetime-local" bsSize="sm" value={from} onChange={(e) => setFrom(e.target.value)} />
          </FormGroup>
          <FormGroup className="mb-0">
            <Label className="small">Bis</Label>
            <Input type="datetime-local" bsSize="sm" value={to} onChange={(e) => setTo(e.target.value)} />
          </FormGroup>
          <Button color="secondary" outline size="sm" onClick={() => query.refetch()}>↻</Button>
        </div>
      </div>
      <DataTable
        loading={query.isLoading}
        empty={!query.isLoading && list.length === 0}
        emptyMessage="Keine Events im Fenster."
      >
        <thead>
          <tr>
            <th style={{ width: 200 }}>Type</th>
            <th style={{ width: 160 }}>IP</th>
            <th>Details</th>
            <th style={{ width: 150 }}>Zeit</th>
          </tr>
        </thead>
        <tbody>
          {list.map((e) => (
            <tr key={e.id}>
              <td>
                <Badge
                  color={
                    e.type?.startsWith('appsec') ? 'danger'
                    : e.type?.startsWith('auth') ? 'warning'
                    : 'info'
                  }
                  pill className="orbit-code-badge-sm"
                >
                  {e.type}
                </Badge>
              </td>
              <td className="small font-monospace">{e.ip ?? '—'}</td>
              <td className="small">
                <code>{JSON.stringify(e.details ?? {})}</code>
              </td>
              <td>
                {e.occurredAt ? <RelativeTime iso={e.occurredAt} /> : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </DataTable>
    </CardBody></Card>
  );
}
